/**
 * The calls the problem report makes — the ONLY file in the feature that names an operation
 * (bata-platform#686, ADR 9063; the lifecycle of bata-platform ADR 1020, ADR 9071).
 *
 * Every one is a generated operation, pinned with the platform's contract 0.101: filing and reading
 * one's own reports and replying on them (`fileMyReport`, `listMyReports`, `readMyReport`,
 * `replyToMyReport`), and the delivery team's list, detail, change, thread and approval
 * (`listReports`, `readReport`, `triageReport`, `writeOnThread`, `approveReport`, `revokeApproval`).
 * The transport gives them the base URL, the token, the language, the `Idempotency-Key` held per
 * decision, `X-Correlation-Id` and the RFC 9457 parse; nothing here calls `fetch`. Lists are cursor
 * collections, read to their end by `useApiCollection`.
 *
 * {@link FEEDBACK_LIMITS} and {@link STRUCTURE_LIMITS} restate the request schemas' bounds so the
 * inputs can stop a person at the limit rather than let the platform refuse the report after they
 * have typed it. `feedback.test.tsx` and `lifecycle.test.ts` compare them with the pinned spec, so a
 * re-pin that moves a bound fails there instead of silently disagreeing with the form. The
 * vocabularies (kinds, statuses) are held equal to the generated enums at compile time, because each
 * word has words in the three catalogues.
 */

import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useApiCollection, useApiCommand, useApiQuery } from '../../api';
import {
  approveReport, fileMyReport, listMyReports, listReports, myWorkCounts, readMyReport, readReport, replyToMyReport,
  revokeApproval, triageReport, writeOnThread,
  type Amendment, type ApprovalOutcome, type AuthorReply, type FeedbackCounts, type FeedbackDetail, type FeedbackKind,
  type FeedbackReport, type FeedbackRequest, type FeedbackStatus, type FiledVia, type MessageOutcome, type StatusChange,
  type TeamMessage, type TeamMessageView, type ThreadMessage, type TransitionView, type TriageDetail, type TriageOutcome,
  type TriageReport, type TriageRequest,
} from '../../api/generated';

export type {
  ApprovalOutcome, FeedbackKind, FeedbackReport, FeedbackRequest, FeedbackStatus, FiledVia, TriageOutcome, TriageReport,
  TriageRequest,
};
export type AuthorReplyRequest = AuthorReply;
export type ReportAmendment = Amendment;
export type MessageReceipt = MessageOutcome;
export type MyReportDetail = FeedbackDetail;
export type TriageDetailRead = TriageDetail;
export type TeamMessageRequest = TeamMessage;
export type LifecycleFeedbackCounts = FeedbackCounts;
/** A message as either side reads it: the team's read names the team member (`sender_name`). */
export type ReportMessage = ThreadMessage & { readonly sender_name?: string | null } | TeamMessageView;
/** A move as either side reads it: the author's carries the statuses and the instant; the team's who and the relabel. */
export type ReportTransition = StatusChange & Partial<Omit<TransitionView, keyof StatusChange>>;

/** The structured fields a report carries (ADR 2137's rubric), as the team's and the author's reads both name them. */
export type ReportStructure = Pick<
  TriageReport,
  'examples' | 'expected_figures' | 'expected_figures_not_applicable_reason' | 'acceptance_criteria' | 'affected_sections' | 'markets' | 'affected_runs'
>;

/** True exactly when `A` and `B` are the same type — the compile-time half of the vocabulary checks. */
type Equals<A, B> = (<T>() => T extends A ? 1 : 2) extends (<T>() => T extends B ? 1 : 2) ? true : false;

/** The four kinds, in the order the form offers them. */
export const REPORT_KINDS = ['clarification', 'data', 'incident', 'enhancement'] as const satisfies readonly FeedbackKind[];
const KINDS_MATCH: Equals<(typeof REPORT_KINDS)[number], FeedbackKind> = true;

/** Every status, in lifecycle order; `in_progress` is legacy and read only. */
export const REPORT_STATUSES = [
  'received', 'needs_info', 'ready', 'approved_for_development', 'in_development', 'delivered', 'resolved', 'declined', 'in_progress',
] as const satisfies readonly FeedbackStatus[];
const STATUSES_MATCH: Equals<(typeof REPORT_STATUSES)[number], FeedbackStatus> = true;

/** Who moved a report, as the team's history names the side. */
export type ActorSide = 'author' | 'team' | 'approver' | 'platform';

/** The vocabulary checks, held so the compiler reports a mismatch where it is declared. */
export const FEEDBACK_VOCABULARIES_MATCH = KINDS_MATCH && STATUSES_MATCH;

/** The structured fields' bounds (`FeedbackRequest`, `TeamMessage`, `AuthorReply` in the pinned spec). */
export const STRUCTURE_LIMITS = {
  examples: { max: 3000 },
  expectedFigures: { max: 2000 },
  acceptanceCriteria: { maxItems: 10, max: 500 },
  sections: { maxItems: 5 },
  markets: { maxItems: 5 },
  runs: { maxItems: 5, max: 200 },
  message: { max: 2000 },
} as const;

/** The request schema's bounds (`FeedbackRequest` in the pinned spec). */
export const FEEDBACK_LIMITS = {
  title: { min: 3, max: 200 },
  description: { min: 10, max: 5000 },
  steps: { max: 3000 },
  route: { max: 400 },
  appVersion: { max: 64 },
  locale: { max: 35 },
  correlationIds: { max: 5, pattern: /^[A-Za-z0-9-]{1,64}$/ },
  /** `TriageRequest.resolution_note` — the team's answer to the author. */
  resolutionNote: { max: 2000 },
} as const;

/** Every read that shows a report's state, re-read after any write that may move it. */
function useInvalidateReports(): () => Promise<void> {
  const client = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: [listMyReports.operationId] }),
      client.invalidateQueries({ queryKey: [readMyReport.operationId] }),
      client.invalidateQueries({ queryKey: [listReports.operationId] }),
      client.invalidateQueries({ queryKey: [readReport.operationId] }),
      client.invalidateQueries({ queryKey: [myWorkCounts.operationId] }),
    ]);
  }, [client]);
}

/**
 * Sends a report. The returned function rejects with the transport's `ApiProblemError` (a 4xx the
 * platform explained) or `ApiUnreachableError`, which `useProblemMessage` turns into words. The
 * idempotency key is held per submission, so a double press or a retry after a dropped connection
 * is one report. On success the person's list of reports is refreshed.
 */
export function useSubmitFeedback(): (submission: FeedbackRequest) => Promise<FeedbackReport> {
  const send = useApiCommand(fileMyReport);
  const refresh = useInvalidateReports();
  return useCallback(async (submission) => {
    const report = await send({ body: submission });
    await refresh();
    return report;
  }, [send, refresh]);
}

/** The signed-in person's own reports, newest first. */
export function useMyFeedback(): UseQueryResult<readonly FeedbackReport[], Error> {
  return useApiCollection(listMyReports, {});
}

/** One of the person's own reports: its content, its history and the thread. Read when `code` is set. */
export function useMyReport(code: string | null): UseQueryResult<MyReportDetail, Error> {
  return useApiQuery(readMyReport, { path: { code: code ?? '' } }, { enabled: code !== null });
}

/** The author's reply; in `needs_info` it sends the report back to the team. */
export function useReplyToMyReport(): (code: string, reply: AuthorReplyRequest) => Promise<MessageReceipt> {
  const send = useApiCommand(replyToMyReport);
  const refresh = useInvalidateReports();
  return useCallback(async (code, reply) => {
    const receipt = await send({ path: { code }, body: reply });
    await refresh();
    return receipt;
  }, [send, refresh]);
}

// ── the delivery team's side (`feedback_triage`) ────────────────────────────────────────────

/** Which reports the triage list shows; `null` is «any». The platform narrows by these. */
export interface TriageFilters {
  readonly status: FeedbackStatus | null;
  readonly kind: FeedbackKind | null;
  readonly sectionId: string | null;
  readonly filedVia?: FiledVia | null;
  readonly market?: string | null;
}

/** Every report, newest first, narrowed by the filters. Read to its end by cursor, like every list. */
export function useTriageReports(filters: TriageFilters): UseQueryResult<readonly TriageReport[], Error> {
  return useApiCollection(listReports, {
    query: {
      ...(filters.status === null ? {} : { status: filters.status }),
      ...(filters.kind === null ? {} : { kind: filters.kind }),
      ...(filters.sectionId === null ? {} : { section_id: filters.sectionId }),
      ...(filters.filedVia === undefined || filters.filedVia === null ? {} : { filedVia: filters.filedVia }),
      ...(filters.market === undefined || filters.market === null ? {} : { market: filters.market }),
    },
  });
}

/** One report as the team reads it: thread, history, the moves this viewer may make, and approval. */
export function useTriageReport(code: string | null): UseQueryResult<TriageDetailRead, Error> {
  return useApiQuery(readReport, { path: { code: code ?? '' } }, { enabled: code !== null });
}

/**
 * Changes a report's status, its kind and/or the note its author reads. The body carries only what
 * changed (the platform refuses an empty one); `resolution_note: null` clears the note. The platform
 * answers the report's state only, never its text: the caller keeps the note it sent.
 */
export function useTriageCommand(): (code: string, change: TriageRequest) => Promise<TriageOutcome> {
  const send = useApiCommand(triageReport);
  const refresh = useInvalidateReports();
  return useCallback(async (code, change) => {
    const outcome = await send({ path: { code }, body: change });
    await refresh();
    return outcome;
  }, [send, refresh]);
}

/** A team message; `request_info` asks the author and moves the report to `needs_info`. */
export function useTeamMessage(): (code: string, message: TeamMessageRequest) => Promise<MessageReceipt> {
  const send = useApiCommand(writeOnThread);
  const refresh = useInvalidateReports();
  return useCallback(async (code, message) => {
    const receipt = await send({ path: { code }, body: message });
    await refresh();
    return receipt;
  }, [send, refresh]);
}

/** Approves the report for development on the content digest the approver read. */
export function useApproveReport(): (code: string, contentDigest: string) => Promise<ApprovalOutcome> {
  const send = useApiCommand(approveReport);
  const refresh = useInvalidateReports();
  return useCallback(async (code, contentDigest) => {
    const outcome = await send({ path: { code }, body: { content_digest: contentDigest } });
    await refresh();
    return outcome;
  }, [send, refresh]);
}

/** Revokes an approval: the report goes back to «ready». */
export function useRevokeApproval(): (code: string) => Promise<ApprovalOutcome> {
  const send = useApiCommand(revokeApproval);
  const refresh = useInvalidateReports();
  return useCallback(async (code) => {
    const outcome = await send({ path: { code } });
    await refresh();
    return outcome;
  }, [send, refresh]);
}
